import React, { useState, useEffect, useCallback, useRef } from 'react';
import Map, { Marker, Source, Layer, NavigationControl, Popup } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MapPin, Truck, Clock, Activity, Package, CheckCircle, XCircle, ChevronRight, Navigation as NavIcon } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { io } from 'socket.io-client';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

const BOGOTA_CENTER = {
    latitude: 4.6097,
    longitude: -74.0817,
    zoom: 11
};

const STATUS_COLORS = {
    pending: '#EF4444',   // red - stops not yet delivered
    delivered: '#10B981', // green - completed
    failed: '#F97316',    // orange - rejected/failed
};

const STATUS_LABELS = {
    pending: 'Pendiente',
    delivered: 'Entregado',
    failed: 'Fallido',
};

const LiveTrackingPage = () => {
    const [viewport, setViewport] = useState(BOGOTA_CENTER);
    const [activeRoutes, setActiveRoutes] = useState([]);
    const [selectedRoute, setSelectedRoute] = useState(null);
    const [breadcrumb, setBreadcrumb] = useState(null);
    const [loading, setLoading] = useState(true);
    const [popupOrder, setPopupOrder] = useState(null);
    const socketRef = useRef(null);
    const mapRef = useRef(null);

    // Load active routes
    const loadLiveTracking = useCallback(async () => {
        try {
            const response = await api.get('/routes/live-tracking');
            if (response.data.success) {
                setActiveRoutes(prev => {
                    const newRoutes = response.data.routes;
                    // Preserve selectedRoute data if it's still active
                    if (selectedRoute) {
                        const updated = newRoutes.find(r => r.id === selectedRoute.id);
                        if (updated) {
                            setSelectedRoute(updated);
                        }
                    }
                    return newRoutes;
                });
            }
        } catch (error) {
            console.error('Error loading live tracking:', error);
            if (!loading) toast.error('Error al cargar rastreo en vivo');
        } finally {
            setLoading(false);
        }
    }, [selectedRoute, loading]);

    // Load breadcrumb for selected route
    const loadBreadcrumb = useCallback(async (routeId) => {
        try {
            const response = await api.get(`/routes/${routeId}/breadcrumb`);
            if (response.data.success) {
                setBreadcrumb(response.data);
            }
        } catch (error) {
            console.error('Error loading breadcrumb:', error);
        }
    }, []);

    // Initial load + polling
    useEffect(() => {
        loadLiveTracking();
        const interval = setInterval(loadLiveTracking, 30000);
        return () => clearInterval(interval);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Socket.IO for real-time updates
    useEffect(() => {
        const socket = io('/', {
            path: '/socket.io',
            transports: ['websocket', 'polling']
        });

        socket.on('connect', () => {
            console.log('📡 Live tracking socket connected:', socket.id);
            socket.emit('join-admin-tracking');
        });

        socket.on('location:update', (data) => {
            setActiveRoutes(prev => prev.map(route => {
                if (route.id === data.routeId) {
                    return {
                        ...route,
                        current_latitude: data.latitude,
                        current_longitude: data.longitude,
                        last_location_time: data.timestamp
                    };
                }
                return route;
            }));
        });

        socket.on('delivery:update', (data) => {
            setActiveRoutes(prev => prev.map(route => {
                if (route.id === data.routeId) {
                    const updatedOrders = (route.orders || []).map(order => {
                        if (order.order_id === data.orderId) {
                            return { ...order, delivery_status: data.status };
                        }
                        return order;
                    });
                    const delivered = updatedOrders.filter(o => o.delivery_status === 'delivered').length;
                    return {
                        ...route,
                        orders: updatedOrders,
                        delivered_orders: delivered
                    };
                }
                return route;
            }));

            // Update selectedRoute if it matches
            setSelectedRoute(prev => {
                if (!prev || prev.id !== data.routeId) return prev;
                const updatedOrders = (prev.orders || []).map(order => {
                    if (order.order_id === data.orderId) {
                        return { ...order, delivery_status: data.status };
                    }
                    return order;
                });
                const delivered = updatedOrders.filter(o => o.delivery_status === 'delivered').length;
                return { ...prev, orders: updatedOrders, delivered_orders: delivered };
            });
        });

        socketRef.current = socket;
        return () => {
            socket.disconnect();
            socketRef.current = null;
        };
    }, []);

    // Load breadcrumb when route selected
    useEffect(() => {
        if (selectedRoute) {
            loadBreadcrumb(selectedRoute.id);
        } else {
            setBreadcrumb(null);
        }
    }, [selectedRoute?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Center map on selected route
    const handleSelectRoute = (route) => {
        setSelectedRoute(route);
        setPopupOrder(null);
        if (route.current_latitude && route.current_longitude) {
            setViewport(v => ({
                ...v,
                latitude: parseFloat(route.current_latitude),
                longitude: parseFloat(route.current_longitude),
                zoom: 13
            }));
        }
    };

    // Breadcrumb trail GeoJSON
    const breadcrumbGeoJSON = breadcrumb?.breadcrumb?.length > 1 ? {
        type: 'Feature',
        geometry: {
            type: 'LineString',
            coordinates: breadcrumb.breadcrumb.map(b => [parseFloat(b.longitude), parseFloat(b.latitude)])
        }
    } : null;

    const breadcrumbLayer = {
        id: 'breadcrumb-trail',
        type: 'line',
        paint: {
            'line-color': '#2563EB',
            'line-width': 5,
            'line-opacity': 0.85
        }
    };

    // Get the selected route's orders for map display
    const displayOrders = selectedRoute?.orders || [];
    const ordersWithCoords = displayOrders.filter(o => o.latitude && o.longitude);

    // Route line connecting stops in delivery sequence
    const sortedStops = [...ordersWithCoords].sort((a, b) => (a.sequence_number || 0) - (b.sequence_number || 0));
    const routeLineGeoJSON = sortedStops.length > 1 ? {
        type: 'Feature',
        geometry: {
            type: 'LineString',
            coordinates: sortedStops.map(o => [parseFloat(o.longitude), parseFloat(o.latitude)])
        }
    } : null;

    const routeLineLayer = {
        id: 'route-stops-line',
        type: 'line',
        paint: {
            'line-color': '#9CA3AF',
            'line-width': 1.5,
            'line-opacity': 0.4,
            'line-dasharray': [6, 4]
        }
    };

    // Progress stats for selected route
    const totalOrders = selectedRoute ? parseInt(selectedRoute.total_orders) || 0 : 0;
    const deliveredOrders = selectedRoute ? parseInt(selectedRoute.delivered_orders) || 0 : 0;
    const failedOrders = selectedRoute ? (selectedRoute.orders || []).filter(o => o.delivery_status === 'failed').length : 0;
    const pendingOrders = totalOrders - deliveredOrders - failedOrders;
    const progressPct = totalOrders > 0 ? Math.round((deliveredOrders / totalOrders) * 100) : 0;

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Cargando rastreo GPS...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow-sm border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-6 py-3">
                    <div className="flex items-center justify-between">
                        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                            <NavIcon className="w-6 h-6 text-blue-600" />
                            Seguimiento en Vivo
                        </h1>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Activity className="w-4 h-4 text-green-500 animate-pulse" />
                            {activeRoutes.length} {activeRoutes.length === 1 ? 'ruta activa' : 'rutas activas'}
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex h-[calc(100vh-57px)]">
                {/* Sidebar */}
                <div className="w-96 bg-white border-r border-gray-200 overflow-y-auto flex flex-col">
                    <div className="p-4 border-b border-gray-200 bg-gray-50">
                        <h2 className="font-bold text-gray-900 text-sm uppercase tracking-wide">Mensajeros en Ruta</h2>
                    </div>

                    {activeRoutes.length === 0 ? (
                        <div className="p-8 text-center text-gray-500 flex-1 flex flex-col items-center justify-center">
                            <Truck className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                            <p className="font-medium">No hay rutas activas</p>
                            <p className="text-xs mt-1">Las rutas aparecerán aquí cuando un mensajero inicie una ruta</p>
                        </div>
                    ) : (
                        <div className="overflow-y-auto">
                            {activeRoutes.map(route => {
                                const isSelected = selectedRoute?.id === route.id;
                                const rDelivered = parseInt(route.delivered_orders) || 0;
                                const rTotal = parseInt(route.total_orders) || 0;
                                const rPct = rTotal > 0 ? Math.round((rDelivered / rTotal) * 100) : 0;

                                return (
                                    <div
                                        key={route.id}
                                        onClick={() => handleSelectRoute(route)}
                                        className={`p-4 cursor-pointer transition-all border-b border-gray-100 hover:bg-blue-50 ${isSelected ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''}`}
                                    >
                                        <div className="flex items-start justify-between mb-2">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white font-bold text-sm shadow">
                                                    {route.route_number}
                                                </div>
                                                <div>
                                                    <p className="font-bold text-gray-900 text-sm">{route.messenger_name}</p>
                                                    <p className="text-xs text-gray-500">Ruta #{route.route_number}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                {route.current_latitude ? (
                                                    <span className="flex items-center gap-1 text-green-600 text-xs font-medium bg-green-50 px-2 py-0.5 rounded-full">
                                                        <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                                                        EN VIVO
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-gray-400">Sin GPS</span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Progress bar */}
                                        <div className="mt-2">
                                            <div className="flex items-center justify-between text-xs mb-1">
                                                <span className="text-gray-500">{rDelivered}/{rTotal} entregas</span>
                                                <span className="font-bold text-gray-700">{rPct}%</span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-2">
                                                <div
                                                    className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full transition-all duration-500"
                                                    style={{ width: `${rPct}%` }}
                                                />
                                            </div>
                                        </div>

                                        {route.last_location_time && (
                                            <div className="mt-2 text-xs text-gray-400 flex items-center gap-1">
                                                <Clock className="w-3 h-3" />
                                                Últ: {new Date(route.last_location_time).toLocaleTimeString('es-CO')}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Order list for selected route */}
                    {selectedRoute && (selectedRoute.orders || []).length > 0 && (
                        <div className="border-t-2 border-blue-200 flex-1 overflow-y-auto bg-gray-50">
                            <div className="p-3 bg-blue-50 border-b border-blue-100 sticky top-0 z-10">
                                <p className="text-xs font-bold text-blue-800 uppercase tracking-wide">
                                    Pedidos — Ruta #{selectedRoute.route_number}
                                </p>
                                <div className="flex gap-3 mt-1 text-xs">
                                    <span className="text-blue-600 font-medium">⏳ {pendingOrders}</span>
                                    <span className="text-green-600 font-medium">✅ {deliveredOrders}</span>
                                    {failedOrders > 0 && <span className="text-red-600 font-medium">❌ {failedOrders}</span>}
                                </div>
                            </div>
                            {(selectedRoute.orders || []).map((order, idx) => (
                                <div
                                    key={order.order_id}
                                    onClick={() => {
                                        if (order.latitude && order.longitude) {
                                            setPopupOrder(order);
                                            setViewport(v => ({
                                                ...v,
                                                latitude: parseFloat(order.latitude),
                                                longitude: parseFloat(order.longitude),
                                                zoom: 15
                                            }));
                                        }
                                    }}
                                    className={`px-3 py-2 border-b border-gray-100 cursor-pointer hover:bg-white transition-colors flex items-center gap-2 ${order.delivery_status === 'delivered' ? 'opacity-60' : ''}`}
                                >
                                    <div
                                        className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                                        style={{ backgroundColor: STATUS_COLORS[order.delivery_status] || STATUS_COLORS.pending }}
                                    >
                                        {order.sequence_number || idx + 1}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium text-gray-900 truncate">{order.customer_name}</p>
                                        <p className="text-xs text-gray-400 truncate">{order.order_number}</p>
                                    </div>
                                    <div className="shrink-0">
                                        {order.delivery_status === 'delivered' && <CheckCircle className="w-4 h-4 text-green-500" />}
                                        {order.delivery_status === 'failed' && <XCircle className="w-4 h-4 text-red-500" />}
                                        {order.delivery_status === 'pending' && <Package className="w-4 h-4 text-blue-400" />}
                                    </div>
                                    <ChevronRight className="w-3 h-3 text-gray-300 shrink-0" />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Map */}
                <div className="flex-1 relative">
                    <Map
                        ref={mapRef}
                        {...viewport}
                        onMove={evt => setViewport(evt.viewState)}
                        mapStyle="mapbox://styles/mapbox/streets-v12"
                        mapboxAccessToken={MAPBOX_TOKEN}
                        style={{ width: '100%', height: '100%' }}
                    >
                        <NavigationControl position="top-right" />

                        {/* Route line connecting stops in sequence */}
                        {routeLineGeoJSON && (
                            <Source id="route-stops" type="geojson" data={routeLineGeoJSON}>
                                <Layer {...routeLineLayer} />
                            </Source>
                        )}

                        {/* Breadcrumb trail */}
                        {breadcrumbGeoJSON && (
                            <Source id="breadcrumb" type="geojson" data={breadcrumbGeoJSON}>
                                <Layer {...breadcrumbLayer} />
                            </Source>
                        )}

                        {/* Order markers for selected route */}
                        {ordersWithCoords.map((order, idx) => {
                            const status = order.delivery_status || 'pending';
                            const color = STATUS_COLORS[status] || STATUS_COLORS.pending;
                            const isFailed = status === 'failed';
                            const isDelivered = status === 'delivered';
                            const isPending = status === 'pending';
                            // Pending = larger so they stand out
                            const size = isPending ? 'w-8 h-8 text-sm' : 'w-7 h-7 text-xs';
                            const borderStyle = isFailed ? 'border-2 border-orange-300' : 'border-2 border-white';

                            return (
                                <Marker
                                    key={`order-${order.order_id}`}
                                    latitude={parseFloat(order.latitude)}
                                    longitude={parseFloat(order.longitude)}
                                    anchor="center"
                                    onClick={(e) => {
                                        e.originalEvent.stopPropagation();
                                        setPopupOrder(order);
                                    }}
                                >
                                    <div
                                        className="relative cursor-pointer group transition-transform hover:scale-125"
                                        title={`${order.order_number} — ${order.customer_name} (${STATUS_LABELS[status]})`}
                                    >
                                        <div
                                            className={`${size} rounded-full flex items-center justify-center text-white font-bold shadow-lg ${borderStyle}`}
                                            style={{ backgroundColor: color }}
                                        >
                                            {isFailed ? '✕' : (order.sequence_number || idx + 1)}
                                        </div>
                                        {/* Small indicator for delivered */}
                                        {isDelivered && (
                                            <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-white rounded-full flex items-center justify-center shadow">
                                                <CheckCircle className="w-3 h-3 text-green-500" />
                                            </div>
                                        )}
                                        {/* Pulse for pending */}
                                        {isPending && (
                                            <div className="absolute inset-0 rounded-full animate-ping opacity-20" style={{ backgroundColor: color }} />
                                        )}
                                    </div>
                                </Marker>
                            );
                        })}

                        {/* Messenger location markers */}
                        {activeRoutes.filter(r => r.current_latitude && r.current_longitude).map(route => (
                            <Marker
                                key={`messenger-${route.id}`}
                                latitude={parseFloat(route.current_latitude)}
                                longitude={parseFloat(route.current_longitude)}
                                anchor="center"
                                onClick={() => handleSelectRoute(route)}
                            >
                                <div className="relative cursor-pointer group">
                                    {/* Pulse animation */}
                                    <div className="absolute inset-0 w-14 h-14 -m-1 bg-blue-400 rounded-full animate-ping opacity-20" />
                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-xl border-4 border-white transition-transform group-hover:scale-110 ${selectedRoute?.id === route.id ? 'bg-blue-600 ring-4 ring-blue-200' : 'bg-blue-500'}`}>
                                        <Truck className="w-6 h-6 text-white" />
                                    </div>
                                    <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-white border-2 border-blue-600 flex items-center justify-center text-xs font-black text-blue-600 shadow">
                                        {route.route_number}
                                    </div>
                                </div>
                            </Marker>
                        ))}

                        {/* Popup for order details */}
                        {popupOrder && popupOrder.latitude && popupOrder.longitude && (
                            <Popup
                                latitude={parseFloat(popupOrder.latitude)}
                                longitude={parseFloat(popupOrder.longitude)}
                                anchor="bottom"
                                onClose={() => setPopupOrder(null)}
                                closeButton={true}
                                closeOnClick={false}
                                offset={15}
                            >
                                <div className="p-1 min-w-[180px]">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span
                                            className="w-3 h-3 rounded-full inline-block"
                                            style={{ backgroundColor: STATUS_COLORS[popupOrder.delivery_status] || STATUS_COLORS.pending }}
                                        />
                                        <span className="text-xs font-bold text-gray-700">
                                            {STATUS_LABELS[popupOrder.delivery_status] || 'Pendiente'}
                                        </span>
                                    </div>
                                    <p className="text-sm font-bold text-gray-900">{popupOrder.customer_name}</p>
                                    <p className="text-xs text-gray-500 mt-0.5">{popupOrder.order_number}</p>
                                    {popupOrder.customer_address && (
                                        <p className="text-xs text-gray-400 mt-1 line-clamp-2">{popupOrder.customer_address}</p>
                                    )}
                                    {popupOrder.total_amount && (
                                        <p className="text-xs font-bold text-gray-700 mt-1">
                                            ${Number(popupOrder.total_amount).toLocaleString('es-CO')}
                                        </p>
                                    )}
                                </div>
                            </Popup>
                        )}
                    </Map>

                    {/* Route metrics overlay */}
                    {selectedRoute && (
                        <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur rounded-xl shadow-xl p-4 max-w-xs border border-gray-200">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-bold text-gray-900 text-sm">
                                    Ruta #{selectedRoute.route_number}
                                </h3>
                                <span className="text-xs text-gray-500">{selectedRoute.messenger_name}</span>
                            </div>

                            {/* Progress */}
                            <div className="mb-3">
                                <div className="flex justify-between text-xs mb-1">
                                    <span className="text-gray-500">Progreso</span>
                                    <span className="font-bold">{deliveredOrders}/{totalOrders} ({progressPct}%)</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2.5">
                                    <div
                                        className="bg-gradient-to-r from-blue-500 to-green-500 h-2.5 rounded-full transition-all duration-700"
                                        style={{ width: `${progressPct}%` }}
                                    />
                                </div>
                            </div>

                            {/* Stats */}
                            <div className="grid grid-cols-3 gap-2 text-center">
                                <div className="bg-blue-50 rounded-lg p-2">
                                    <p className="text-lg font-bold text-blue-600">{pendingOrders}</p>
                                    <p className="text-xs text-blue-400">Pendientes</p>
                                </div>
                                <div className="bg-green-50 rounded-lg p-2">
                                    <p className="text-lg font-bold text-green-600">{deliveredOrders}</p>
                                    <p className="text-xs text-green-400">Entregados</p>
                                </div>
                                <div className="bg-red-50 rounded-lg p-2">
                                    <p className="text-lg font-bold text-red-600">{failedOrders}</p>
                                    <p className="text-xs text-red-400">Fallidos</p>
                                </div>
                            </div>

                            {breadcrumb?.metrics && (
                                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                                    <div className="bg-gray-50 p-2 rounded">
                                        <span className="text-gray-400">Distancia</span>
                                        <p className="font-bold text-gray-700">{breadcrumb.metrics.totalDistance.toFixed(1)} km</p>
                                    </div>
                                    <div className="bg-gray-50 p-2 rounded">
                                        <span className="text-gray-400">Vel. Promedio</span>
                                        <p className="font-bold text-gray-700">{breadcrumb.metrics.avgSpeed} km/h</p>
                                    </div>
                                </div>
                            )}

                            <button
                                onClick={() => { setSelectedRoute(null); setPopupOrder(null); }}
                                className="mt-3 w-full bg-gray-100 text-gray-600 py-1.5 rounded-lg font-medium text-xs hover:bg-gray-200 transition-colors"
                            >
                                Cerrar Detalle
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LiveTrackingPage;
